import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Globe, ArrowRight, CheckCircle, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageSEO } from "@/components/PageSEO";

export default function WebDesignEastAnglia() {
  return (
    <div data-testid="web-design-ea-page" className="pt-16 lg:pt-20">
      <PageSEO
        title="Website Design East Anglia & Suffolk"
        description="Local website design in East Anglia. Based in Lowestoft, serving Suffolk, Norfolk, Norwich, and Ipswich. Professional sites with no build fees. Get a free quote."
        path="/website-design-east-anglia"
      />

      {/* Hero */}
      <section className="py-20 lg:py-28 hero-gradient relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={16} className="text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium uppercase tracking-widest">East Anglia</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6">
              Website Design in <span className="gradient-text">East Anglia</span>
            </h1>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed max-w-2xl">
              Professional website design for businesses across East Anglia. Based in Lowestoft, Suffolk, we build modern, fast websites for local companies in Norwich, Ipswich, Great Yarmouth, and the wider Suffolk and Norfolk region.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/pricing"><Button className="btn-primary text-white font-medium px-8 py-3">View Pricing <ArrowRight className="ml-2" size={18} /></Button></Link>
              <Link to="/contact"><Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-white/5 px-8 py-3">Get a Free Quote</Button></Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Local Web Design */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">Local Web Design You Can Trust</h2>
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <p className="text-gray-400 leading-relaxed mb-4">
                  Finding a reliable web designer in East Anglia can be challenging. Many businesses in Suffolk and Norfolk have been burned by agencies that over-promise and under-deliver, or freelancers who disappear after launch. At Your-IT-Solutions, we are different.
                </p>
                <p className="text-gray-400 leading-relaxed mb-4">
                  Based right here in Lowestoft, we understand the East Anglia business landscape. We know the challenges local businesses face — from competing with larger companies online to reaching customers in rural areas where digital presence is essential. Our websites are designed to help East Anglia businesses stand out in search results and convert local visitors into paying customers.
                </p>
                <p className="text-gray-400 leading-relaxed mb-4">
                  Whether you run a tradesman business in Great Yarmouth, a consultancy in Norwich, a retail shop in Ipswich, or a service company anywhere in Suffolk — we build websites that work for your specific market and customer base.
                </p>
                <p className="text-gray-400 leading-relaxed">
                  Being local means we are available for face-to-face meetings, understand your market, and are invested in the success of businesses in our community. We are not a faceless agency based hundreds of miles away — we are your neighbours.
                </p>
              </div>
              <div className="space-y-4">
                <div className="p-5 rounded-xl bg-gray-900/50 border border-gray-800">
                  <h3 className="text-white font-semibold mb-1">Based in Lowestoft, Suffolk</h3>
                  <p className="text-gray-400 text-sm">Local support with face-to-face availability for businesses across East Anglia.</p>
                </div>
                <div className="p-5 rounded-xl bg-gray-900/50 border border-gray-800">
                  <h3 className="text-white font-semibold mb-1">No Upfront Build Fees</h3>
                  <p className="text-gray-400 text-sm">Simple monthly plans from £90/month. Hosting, domain, and ongoing support all included.</p>
                </div>
                <div className="p-5 rounded-xl bg-gray-900/50 border border-gray-800">
                  <h3 className="text-white font-semibold mb-1">SEO for Local Search</h3>
                  <p className="text-gray-400 text-sm">Optimised to help your business appear when people in East Anglia search for your services.</p>
                </div>
                <div className="p-5 rounded-xl bg-gray-900/50 border border-gray-800">
                  <h3 className="text-white font-semibold mb-1">Ongoing Maintenance</h3>
                  <p className="text-gray-400 text-sm">Content updates, security patches, and performance monitoring — we keep your site running at its best.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Areas We Serve */}
      <section className="py-16 lg:py-24 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Areas We Serve Across East Anglia</h2>
            <p className="text-gray-400 mb-12 text-lg max-w-2xl">We work with businesses throughout Suffolk, Norfolk, and the wider East Anglia region.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {["Lowestoft", "Norwich", "Ipswich", "Great Yarmouth", "Bury St Edmunds", "King's Lynn", "Felixstowe", "Beccles", "Southwold", "Thetford", "Diss", "Halesworth"].map((area) => (
                <div key={area} className="flex items-center gap-3 p-4 rounded-xl bg-gray-900/50 border border-gray-800">
                  <CheckCircle size={16} className="text-cyan-500 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">{area}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Local */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">Why Choose a Local Web Designer?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="p-6 rounded-2xl bg-gray-900/50 border border-gray-800">
                <h3 className="text-white font-semibold text-lg mb-2">Face-to-Face Meetings</h3>
                <p className="text-gray-400 text-sm leading-relaxed">We can meet in person to discuss your project, review designs, and plan your website strategy. No timezone differences or communication barriers.</p>
              </div>
              <div className="p-6 rounded-2xl bg-gray-900/50 border border-gray-800">
                <h3 className="text-white font-semibold text-lg mb-2">Local Market Knowledge</h3>
                <p className="text-gray-400 text-sm leading-relaxed">We understand the East Anglia business landscape, local competition, and what it takes to rank in local search results for Suffolk and Norfolk businesses.</p>
              </div>
              <div className="p-6 rounded-2xl bg-gray-900/50 border border-gray-800">
                <h3 className="text-white font-semibold text-lg mb-2">Community Investment</h3>
                <p className="text-gray-400 text-sm leading-relaxed">We are invested in the success of local businesses. When you grow, our community grows. We are not going anywhere — we are here for the long term.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-gray-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Get Your East Anglia Business Online</h2>
            <p className="text-gray-400 mb-8 text-lg">No upfront fees, no contracts. Just a professional website that helps your business grow.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/pricing"><Button className="btn-primary text-white font-medium px-8 py-3">View Plans <ArrowRight className="ml-2" size={18} /></Button></Link>
              <Link to="/contact"><Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-white/5 px-8 py-3">Get a Free Quote</Button></Link>
            </div>
            <p className="text-gray-500 text-sm mt-6">
              See our full <Link to="/website-design-uk" className="text-cyan-400 hover:text-cyan-300">UK website design</Link> services. We also provide <Link to="/ai-automation-agents-uk" className="text-cyan-400 hover:text-cyan-300">AI automation</Link> solutions.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
